/**
 * Values that are part of the API contract and that both clients branch on (spec §3.3, §6.5).
 *
 * Kept here rather than duplicated per app, and deliberately narrow: anything derivable from the
 * generated types is not repeated. What lives here is the ordering and grouping a UI needs, which
 * the OpenAPI document does not carry.
 */

/** Statuses in the order a translator thinks about them: nothing, guessed, written, approved. */
export const TRANSLATION_STATUSES = ['empty', 'machine', 'draft', 'reviewed'] as const;
export type TranslationStatus = (typeof TRANSLATION_STATUSES)[number];

/** CLDR order — i18next expands plural suffixes in this sequence, and so does the export. */
export const PLURAL_FORMS = ['zero', 'one', 'two', 'few', 'many', 'other'] as const;
export type PluralForm = (typeof PLURAL_FORMS)[number];

/**
 * What a project or package sees of a user who holds a grant on it: their global role, when that
 * role is scoped (spec §6.4, ADR 0011). The admin tier holds no grants and is unrestricted instead.
 */
export const SCOPE_ROLES = ['translator', 'viewer'] as const;
export type ScopeRole = (typeof SCOPE_ROLES)[number];

/**
 * The one global role a user holds, highest first. It says *what* the user may do; a grant says
 * *where*. `superadmin` is a superset of `admin`.
 */
export const GLOBAL_ROLES = ['superadmin', 'admin', 'translator', 'viewer'] as const;
export type GlobalRole = (typeof GLOBAL_ROLES)[number];

export const SCOPE_TYPES = ['project', 'package'] as const;
export type ScopeType = (typeof SCOPE_TYPES)[number];

/** `position` reproduces the authoring order, `key` is code-point order (spec §8.2). */
export const EXPORT_SORTS = ['position', 'key'] as const;
export type ExportSort = (typeof EXPORT_SORTS)[number];

/** Scopes an API token can carry (spec §5.7). */
export const TOKEN_SCOPES = [
  'export:read',
  'publish:write',
  'missing-keys:write',
  'imports:write',
] as const;
export type TokenScope = (typeof TOKEN_SCOPES)[number];

export const RELEASE_STATUSES = ['pending', 'uploading', 'published', 'failed'] as const;
export const AI_JOB_STATUSES = ['queued', 'running', 'done', 'failed', 'cancelled'] as const;

/**
 * Error codes the client reacts to rather than merely displays (spec Prilog D). The full catalogue
 * lives in the backend; listing all of it here would only rot.
 */
export const ERROR_CODES = {
  /** Authenticated but not in `app_users` — the whole app is replaced by the no-access page. */
  userNotRegistered: 'USER_NOT_REGISTERED',
  unauthorized: 'UNAUTHORIZED',
  validationError: 'VALIDATION_ERROR',
  /** Somebody else saved the same cell first; the grid offers to keep or discard. */
  revisionConflict: 'REVISION_CONFLICT',
  /** The key vanished under an open editor; the cell keeps the text and offers to recreate it. */
  keyNotFound: 'KEY_NOT_FOUND',
  /** Publish refused: unreviewed values, or QA errors, on a target that forbids them. */
  unreviewedTranslations: 'UNREVIEWED_TRANSLATIONS',
  qaErrors: 'QA_ERRORS',
  releaseUnchanged: 'RELEASE_UNCHANGED',
  /** The upload was previewed against state that has since moved. */
  importStale: 'IMPORT_STALE',
  /** No AI provider is configured in this environment. */
  aiProviderFailed: 'AI_PROVIDER_FAILED',
  /** Renaming or deleting a key other projects link; `details.projects` names them (ADR 0006). */
  keyLinked: 'KEY_LINKED',
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** How the response envelope looks for every `/v1` route (spec §7). */
export interface ApiEnvelope<T> {
  data: T;
  meta?: { total?: number; cursor?: string | null };
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}
